import { InputField } from '@components/ui/formElements/form/InputField'
import { checkStoreIs } from '@helpers/env'
import styles from './styles.module.scss'
import { ISO_3166_COUNTRIES } from '@consts/index'
import { z } from 'zod'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { getProvinces, ProvinceData } from '@api/apiCalls'
import trackError from '@helpers/trackError'
import { FieldValues, UseFormSetValue } from 'react-hook-form'
import { UserProfileAddressSchema } from '@lib/form/validationSchemas'
import { SelectField } from '@components/ui/formElements/form/selectFields/SelectField'
import { SingleCheckbox } from '@components/ui/formElements/form/checkboxFields/SingleCheckbox'
import FormGrid from '@components/ui/formElements/form/shared/FormGrid'

type SetValueFunction<T extends FieldValues> = UseFormSetValue<T>

type AddressFormFieldsProps = {
  formValues: AddAddressData | UpdateAddressData
  formSetFieldValue: SetValueFunction<AddAddressData | UpdateAddressData>
  setIsProvincesLoading: Dispatch<SetStateAction<boolean>>
}

export interface FieldStateMethods {
  getIsProvincesLoading: () => boolean
}

export function getProvinceList(provinceData: ProvinceData | null) {
  if (!provinceData || !provinceData.provinces) return

  return provinceData.provinces.map(({ name, code }) => {
    return {
      value: code,
      label: name,
      available: true
    }
  })
}

export const AddAddressSchema = UserProfileAddressSchema.omit({
  id: true
}).extend({
  territoryCode: z.string(),
  defaultAddress: z.boolean()
})

export type AddAddressData = z.infer<typeof AddAddressSchema>

export const UpdateAddressSchema = UserProfileAddressSchema.extend({
  defaultAddress: z.boolean(),
  territoryCode: z.string()
})

export type UpdateAddressData = z.infer<typeof UpdateAddressSchema>

function getCountryList() {
  return Object.entries(ISO_3166_COUNTRIES).map(
    ([_, { alpha2, countryName }]) => {
      return {
        value: alpha2,
        label: countryName,
        available: true
      }
    }
  )
}

export const AddressFormFields = ({
  formValues,
  setIsProvincesLoading
}: AddressFormFieldsProps) => {
  const [provinceData, setProvinceData] = useState<ProvinceData | null>(null)

  useEffect(() => {
    const territoryCode = formValues.territoryCode
    if (territoryCode) {
      setIsProvincesLoading(true)

      getProvinces(territoryCode as string)
        .then(data => {
          setProvinceData(data)
        })
        .catch(err => {
          const error = err as Error
          trackError(
            new Error(
              `Failed to get province data on edit address - ${error.message}`
            ),
            {
              error,
              section: 'Account Page'
            }
          )
        })
        .finally(() => {
          setIsProvincesLoading(false)
        })
    }
  }, [formValues.territoryCode, setIsProvincesLoading])

  const countries = getCountryList()
  const provinces = getProvinceList(provinceData)

  const defaultCountry =
    countries.find(country => country.value === formValues?.territoryCode)
      ?.label || 'Select Country'

  return (
    <div className={styles.AccountForm__content}>
      <FormGrid gap="medium">
        <InputField
          label="First Name"
          name="firstName"
          type="text"
          required
          placeholder="First Name"
        />
        <InputField
          label="Last Name"
          name="lastName"
          type="text"
          required
          placeholder="Last Name"
        />
        <InputField
          label="Address Line 1"
          name="address1"
          type="text"
          required
          placeholder="Address Line 1"
        />
        <InputField
          label="Address Line 2"
          name="address2"
          id="address2"
          type="text"
          placeholder="Address Line 2"
        />
        <InputField
          label="City"
          name="city"
          type="text"
          required
          placeholder="City"
        />
        <SelectField
          forceDirection="up"
          id="territoryCode"
          options={countries}
          name="Country"
          required={true}
          placeholder="Country"
          defaultValue={defaultCountry}
          settings={{ label: { visible: true } }}
        />
        {provinceData && provinces && (
          <SelectField
            forceDirection="up"
            id="zoneCode"
            options={provinces}
            name={provinceData.label || 'Province'}
            required={true}
            placeholder="Country"
            defaultValue={defaultCountry}
            settings={{ label: { visible: true } }}
          />
        )}
        <InputField
          name="zip"
          label={checkStoreIs('us') ? 'Zip Code' : 'Postal Code'}
          type="text"
          required
          placeholder="Postcode"
        />
        <SingleCheckbox
          id="defaultAddress"
          checkboxLabel="Primary Address"
          size="small"
        />
      </FormGrid>
    </div>
  )
}
